import { useMemo } from "react";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";
import { dayjs, formatDate } from "@/core/utils/date";
import { formatMoneyCents } from "@/core/utils/money";
import { formatPhoneNumber } from "@/core/utils/phone";
import { Payout } from "@/customer/customer";

interface PayoutInfoProps {
  label: string;
  value: string | number;
  className?: string;
}

const PayoutInfo = ({ label, value, className }: PayoutInfoProps) => (
  <Stack align="start" gap={2} className={cn("w-full", className)}>
    <Text variant="label" tone="muted">
      {label}
    </Text>
    <Text>{value}</Text>
  </Stack>
);

interface PayoutItemProps {
  payout: Payout;
}

const PayoutItem = ({ payout }: PayoutItemProps) => (
  <Stack className="w-full" align="start" gap={4} direction="row">
    <PayoutInfo
      label="Referral"
      value={
        payout.referral?.potential_customer_name ||
        formatPhoneNumber(payout.referral?.potential_customer_phone_number) ||
        "-"
      }
      className="flex-1"
    />
    <PayoutInfo
      label="Date"
      value={formatDate(payout.created_at)}
      className="w-[108px]"
    />
    <PayoutInfo
      label="Amount"
      value={formatMoneyCents(payout.amount) || "-"}
      className="w-[81px]"
    />
  </Stack>
);

interface PayoutListProps {
  payouts: Payout[];
}

export const PayoutList = ({ payouts }: PayoutListProps) => {
  const sortedPayouts = useMemo(
    () =>
      [...payouts].sort((a, b) =>
        dayjs(b.created_at).diff(dayjs(a.created_at)),
      ),
    [payouts],
  );

  return (
    <Stack align="start" gap={4} className="h-full w-full px-6 pr-1">
      <div className="min-h-0 w-full flex-1 overflow-y-auto pr-5">
        <Stack align="start" gap={2} className="min-h-full w-full pt-4 pb-8">
          {sortedPayouts.length === 0 && (
            <Empty className={emptyStateVariants({ layout: "inline" })}>
              <EmptyHeader>
                <EmptyDescription>
                  No payouts for this customer yet.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
          {sortedPayouts.map((payout) => (
            <PayoutItem key={payout.id} payout={payout} />
          ))}
        </Stack>
      </div>
    </Stack>
  );
};
